import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Lock, Sparkles, ArrowLeft, KeyRound, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, {
        email,
        password
      });

      localStorage.setItem('session_token', response.data.session_token);
      const user = response.data.user;
      
      if (user?.role === 'admin') {
        toast.success('¡Bienvenida al Panel de Administración!');
        navigate('/admin');
      } else {
        // Verificar si el usuario SOLO tiene challenges (sin programa/suscripción)
        const hasProgram = user?.subscription_level && 
          user.subscription_level !== 'free' && 
          user.subscription_level !== 'challenge_only';
        const purchasedChallenges = user?.purchased_challenges || [];
        
        if (!hasProgram && purchasedChallenges.length > 0) {
          // Usuario de solo challenge - ir directo al primer challenge
          toast.success('¡Bienvenida a tu Challenge!');
          navigate(`/challenge/${purchasedChallenges[0]}`);
        } else {
          toast.success('¡Bienvenida de vuelta!');
          navigate('/dashboard');
        }
      }
    } catch (error) {
      toast.error('Credenciales inválidas');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Ingresa tu email');
      return;
    }
    setForgotLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { 
        email: forgotEmail
      });
      setEmailSent(true);
      toast.success('Revisa tu email para restablecer tu contraseña');
    } catch (error) {
      toast.error('Error al enviar el email');
    } finally {
      setForgotLoading(false);
    }
  };

  const resetForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotEmail('');
    setEmailSent(false);
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-[#09090b]">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 bg-pink-500/10 border border-pink-500/30 rounded-full px-4 py-2 mb-6">
            <Sparkles className="w-4 h-4 text-pink-400" />
            <span className="text-pink-400 text-sm font-medium uppercase tracking-wider">Bienvenida</span>
          </div>
          
          <h2 className="font-barlow text-4xl font-bold text-white" data-testid="login-title">
            Iniciar Sesión
          </h2>
          <p className="mt-3 text-zinc-400">
            ¿No tienes cuenta?{' '}
            <Link to="/register" className="text-pink-500 hover:text-pink-400 font-medium">
              Regístrate aquí
            </Link>
          </p>
        </div>

        <div className="bg-zinc-900/50 border border-zinc-800 rounded-3xl p-8">
          <form className="space-y-6" onSubmit={handleLogin}>
            <div>
              <Label htmlFor="email" className="text-zinc-300">Email</Label>
              <div className="mt-2 relative">
                <Mail className="absolute left-3 top-3 text-zinc-500 w-5 h-5" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-pink-500 rounded-xl h-12"
                  placeholder="tu@email.com"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-zinc-300">Contraseña</Label>
              <div className="mt-2 relative">
                <Lock className="absolute left-3 top-3 text-zinc-500 w-5 h-5" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-pink-500 rounded-xl h-12"
                  placeholder="••••••••"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-pink-600 hover:bg-pink-500 text-white rounded-full py-6 font-bold transition-all hover:scale-[1.02] glow-pink"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
            </Button>

            <div className="text-center mt-4">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-pink-500 hover:text-pink-400 text-sm font-medium"
                data-testid="forgot-password-link"
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-zinc-700"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-zinc-900/50 text-zinc-500">O continúa con</span>
              </div>
            </div>

            <Button
              type="button"
              onClick={handleGoogleLogin}
              className="mt-4 w-full bg-zinc-800 border border-zinc-700 text-white hover:bg-zinc-700 rounded-full py-6 font-bold"
              data-testid="google-login"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continuar con Google
            </Button>
          </div>
        </div>
      </div>

      {/* Modal de Olvidé mi Contraseña */}
      {showForgotPassword && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={resetForgotPassword}>
          <div 
            className="bg-zinc-900 border border-zinc-800 rounded-3xl p-8 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {!emailSent ? (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <button 
                    onClick={resetForgotPassword}
                    className="text-zinc-400 hover:text-white"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <div className="flex items-center gap-2">
                    <KeyRound className="w-5 h-5 text-pink-500" />
                    <h3 className="text-xl font-bold text-white">Recuperar Contraseña</h3>
                  </div>
                </div>

                <form onSubmit={handleRequestReset} className="space-y-4">
                  <p className="text-zinc-400 text-sm mb-4">
                    Ingresa tu email y te enviaremos un enlace para restablecer tu contraseña.
                  </p>
                  <div>
                    <Label htmlFor="forgot-email" className="text-zinc-300">Email</Label>
                    <div className="mt-2 relative">
                      <Mail className="absolute left-3 top-3 text-zinc-500 w-5 h-5" />
                      <Input
                        id="forgot-email"
                        type="email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        className="pl-10 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-pink-500 rounded-xl h-12"
                        placeholder="tu@email.com"
                        required
                        data-testid="forgot-email-input"
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-pink-600 hover:bg-pink-500 text-white rounded-full py-5 font-bold"
                    disabled={forgotLoading}
                    data-testid="send-reset-link"
                  >
                    {forgotLoading ? 'Enviando...' : 'Enviar Enlace'}
                  </Button>
                </form>
              </>
            ) : (
              <div className="text-center py-4">
                <div className="bg-green-500/10 border border-green-500/30 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">¡Email Enviado!</h3>
                <p className="text-zinc-400 mb-6">
                  Revisa tu bandeja de entrada (y spam) en <span className="text-pink-400">{forgotEmail}</span>
                </p>
                <p className="text-zinc-500 text-sm mb-4">
                  El enlace expira en 24 horas.
                </p>
                <Button
                  onClick={resetForgotPassword}
                  variant="outline"
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-full"
                >
                  Cerrar
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
